import { useEffect, useRef, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useCurrentTenant } from "@/features/settings/api/useCurrentTenant";
import { useAppSelector } from "@/stores/hook";
import { cloneVoice, deleteClonedVoice, listVoiceEngines, previewVoice } from "../../api/ai.api";
import {
  GEMINI_CALL_VOICES,
  telecallerObjectives,
  type TelecallerObjective,
  type TelecallerStartOptions,
  type VoiceEngineId,
  type VoiceEngineInfo,
  type VoiceEngineVoice,
} from "../../voice/telecaller.types";

// ---------------------------------------------------------------------------
// TelecallerSetup — the sheet shown BEFORE the line opens in telecaller mode.
// The launcher picks what the call is for, the language, the voice and the
// name the AI introduces itself with, then presses Call. Same frosted card
// as the recording / chart cards; inline-styled, portal-safe.
//
// Voice engine: Gemini's own voice, or Sarvam Bulbul / ElevenLabs re-voicing
// what Gemini says (server: src/ai/voice/tts). ▶ plays a sample line in the
// picked voice; ElevenLabs voices can be cloned from a recording right here.
// ---------------------------------------------------------------------------

interface Props {
  leadName: string;
  language: "ml" | "en";
  initialObjective?: TelecallerObjective;
  /** A refusal from the last attempt (outside hours / do-not-call) — offers the override. */
  refusal: { code: string; message: string } | null;
  onStart: (opts: Extract<TelecallerStartOptions, { mode: "telecaller" }>) => void;
  onCancel: () => void;
}

const AGENT_KEY = "xale_ai_telecaller_agent";
const VOICE_KEY = "xale_ai_telecaller_voice";
const DISCLOSE_KEY = "xale_ai_telecaller_disclose";
const DIALECT_KEY = "xale_ai_telecaller_dialect";
const GREETING_KEY = "xale_ai_telecaller_greeting";
const COMPANY_KEY = "xale_ai_telecaller_company";
const SAYAS_KEY = "xale_ai_telecaller_company_sayas";
const PACE_KEY = "xale_ai_telecaller_pace";
/** Per engine: the Sarvam speaker / ElevenLabs voice id last used. */
const engineVoiceKey = (engine: VoiceEngineId) => `xale_ai_telecaller_engine_voice_${engine}`;

const VOICES = GEMINI_CALL_VOICES;
const GEMINI_MALE = new Set(GEMINI_CALL_VOICES.filter((v) => v.male).map((v) => v.value));
const ENGINE_NAMES: Record<VoiceEngineId, string> = {
  gemini: "Gemini — built-in voices",
  sarvam: "Sarvam Bulbul v3 — Indian voices",
  elevenlabs: "ElevenLabs v4 Turbo — custom voices",
};
type Dialect = "auto" | "mappila" | "kozhikode" | "standard";
type Greeting = "auto" | "salaam" | "hello";
/** Speaking tempo. Brisk is the default — Kozhikode people talk fast (owner 2026-09-26). */
type Pace = "natural" | "brisk" | "fast";

function readLocal(key: string, fallback: string): string {
  try {
    return localStorage.getItem(key) ?? fallback;
  } catch {
    return fallback;
  }
}
function writeLocal(key: string, value: string) {
  try {
    localStorage.setItem(key, value);
  } catch {
    /* private mode */
  }
}

const label: React.CSSProperties = { fontSize: 11.5, letterSpacing: "0.02em", textTransform: "uppercase", opacity: 0.6, marginBottom: 6 };
const field: React.CSSProperties = {
  width: "100%", height: 40, padding: "0 12px", borderRadius: 12, font: "inherit", fontSize: 14,
  color: "inherit", background: "rgba(255,255,255,0.08)", border: "1px solid rgba(255,255,255,0.16)", outline: "none",
};
const panel: React.CSSProperties = {
  display: "grid", gap: 10, padding: "12px", borderRadius: 12,
  background: "rgba(255,255,255,0.05)", border: "1px solid rgba(255,255,255,0.12)",
};
const linkBtn: React.CSSProperties = {
  background: "none", border: "none", padding: 0, font: "inherit", fontSize: 12.5, color: "inherit",
  opacity: 0.8, textDecoration: "underline", cursor: "pointer",
};
const note: React.CSSProperties = { fontSize: 12, lineHeight: 1.4, opacity: 0.7 };
const errorNote: React.CSSProperties = { fontSize: 12.5, lineHeight: 1.4, color: "#ffb4a8" };

function firstName(name: string): string {
  return name.trim().split(/\s+/)[0] || name;
}

export default function TelecallerSetup({ leadName, language, initialObjective, refusal, onStart, onCancel }: Props) {
  const ml = language === "ml";
  const [objective, setObjective] = useState<TelecallerObjective>(initialObjective ?? "first_contact");
  const [brief, setBrief] = useState("");
  const [agentName, setAgentName] = useState(() => readLocal(AGENT_KEY, ""));
  // "Company name as spoken": the tenant's legal name is often long ("… Pvt Ltd");
  // the caller should say the short name people know. Remembered per browser.
  const { tenant } = useCurrentTenant();
  // The tenant's industry words the objectives (academy: batches, not intakes).
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const industryId = useAppSelector((s: any) => s.auth?.currentUser?.user?.tenant?.industryId) as number | undefined;
  const objectives = telecallerObjectives(industryId);
  const tenantName = String((tenant as { tenantName?: string } | null | undefined)?.tenantName ?? "");
  const [companyName, setCompanyName] = useState(() => readLocal(COMPANY_KEY, ""));
  const [companySayAs, setCompanySayAs] = useState(() => readLocal(SAYAS_KEY, ""));
  const [voice, setVoice] = useState(() => readLocal(VOICE_KEY, "female"));
  const [discloseAI, setDiscloseAI] = useState(() => readLocal(DISCLOSE_KEY, "1") !== "0");
  const [dialect, setDialect] = useState<Dialect>(() => {
    const v = readLocal(DIALECT_KEY, "auto");
    return v === "mappila" || v === "kozhikode" || v === "standard" ? v : "auto";
  });
  const [greeting, setGreeting] = useState<Greeting>(() => {
    const v = readLocal(GREETING_KEY, "auto");
    return v === "salaam" || v === "hello" ? v : "auto";
  });
  const [pace, setPace] = useState<Pace>(() => {
    const v = readLocal(PACE_KEY, "brisk");
    return v === "natural" || v === "fast" ? v : "brisk";
  });
  const [override, setOverride] = useState(false);
  /** Rehearsal — the launcher plays the lead; nothing is written. Off by default, never remembered. */
  const [practice, setPractice] = useState(false);

  // ---- Voice engine ----------------------------------------------------------
  const queryClient = useQueryClient();
  const enginesQuery = useQuery({
    queryKey: ["ai-voice-engines"],
    queryFn: listVoiceEngines,
    staleTime: 60 * 1000,
    retry: 0,
  });
  const engines: VoiceEngineInfo[] = enginesQuery.data?.engines ?? [];
  // An older API has no /ai/voice/* engine routes (404): the sheet is exactly
  // the old one — Gemini voices only, no ▶ preview.
  const serverHasEngines = enginesQuery.isSuccess && engines.length > 0;
  const engineInfo = (id: VoiceEngineId) => engines.find((e) => e.id === id);
  const [enginePick, setEnginePick] = useState<VoiceEngineId>("gemini");
  // Every call starts on the WORKSPACE default (Settings → AI Connections);
  // a change here applies to this call only. Applied once, and never over a
  // pick the launcher already made in this sheet.
  const defaultsApplied = useRef(false);
  const engineTouched = useRef(false);
  useEffect(() => {
    const d = enginesQuery.data?.defaults;
    if (defaultsApplied.current || !d) return;
    defaultsApplied.current = true;
    if (engineTouched.current) return;
    setEnginePick(d.engine);
    if (!d.voice) return;
    if (d.engine === "gemini") setVoice(d.voice);
    else setEngineVoices((m) => ({ ...m, [d.engine]: d.voice as string }));
  }, [enginesQuery.data]);
  // A remembered engine whose key was removed (or an older server) → Gemini.
  const engine: VoiceEngineId = enginePick !== "gemini" && !engineInfo(enginePick)?.available ? "gemini" : enginePick;
  const [engineVoices, setEngineVoices] = useState<Record<string, string>>(() => ({
    sarvam: readLocal(engineVoiceKey("sarvam"), ""),
    elevenlabs: readLocal(engineVoiceKey("elevenlabs"), ""),
  }));
  const voiceList: VoiceEngineVoice[] = engine === "gemini" ? [] : (engineInfo(engine)?.voices ?? []);
  // The remembered voice if it still exists, else the engine's first voice.
  const engineVoiceId = engine === "gemini" ? "" : voiceList.find((v) => v.id === engineVoices[engine])?.id ?? voiceList[0]?.id ?? "";
  const engineVoice = voiceList.find((v) => v.id === engineVoiceId) ?? null;
  const gender: "female" | "male" = engine === "gemini" ? (GEMINI_MALE.has(voice) ? "male" : "female") : engineVoice?.gender === "male" ? "male" : "female";
  const setEngineVoice = (id: string) => setEngineVoices((m) => ({ ...m, [engine]: id }));

  // ---- ▶ Preview -------------------------------------------------------------
  const [previewing, setPreviewing] = useState(false);
  const [previewError, setPreviewError] = useState<string | null>(null);
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const abortRef = useRef<AbortController | null>(null);
  const stopPreview = () => {
    abortRef.current?.abort();
    abortRef.current = null;
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current = null;
    }
    setPreviewing(false);
  };
  useEffect(() => () => stopPreview(), []);
  const spokenCompany = companyName.trim() || tenantName || "Skymark";
  const sampleLine = ml
    ? `ഹലോ, ${firstName(leadName)} അല്ലേ? ഞാൻ ${agentName.trim() || (gender === "male" ? "Arun" : "Anjali")}, ${spokenCompany}-ൽ നിന്നാ വിളിക്കുന്നത്. Enquiry തന്നിരുന്നല്ലോ, രണ്ട് മിനിറ്റ് സംസാരിക്കാൻ പറ്റോ?`
    : `Hello, is this ${firstName(leadName)}? I'm ${agentName.trim() || (gender === "male" ? "Arun" : "Anjali")}, calling from ${spokenCompany}. You'd sent us an enquiry — do you have two minutes?`;
  const playPreview = async () => {
    if (previewing) {
      stopPreview();
      return;
    }
    setPreviewError(null);
    setPreviewing(true);
    const ctrl = new AbortController();
    abortRef.current = ctrl;
    try {
      const blob = await previewVoice(
        {
          engine,
          voice: engine === "gemini" ? voice : engineVoiceId,
          text: sampleLine,
          language,
          companyName: spokenCompany,
          companySayAs: companySayAs.trim() || undefined,
        },
        ctrl.signal
      );
      if (ctrl.signal.aborted) return;
      const url = URL.createObjectURL(blob);
      const audio = new Audio(url);
      audioRef.current = audio;
      audio.onended = () => {
        URL.revokeObjectURL(url);
        if (audioRef.current === audio) {
          audioRef.current = null;
          setPreviewing(false);
        }
      };
      await audio.play();
    } catch (err) {
      if (ctrl.signal.aborted) return;
      setPreviewError((err as Error).message || (ml ? "Preview play ചെയ്യാൻ പറ്റിയില്ല." : "Could not play the preview."));
      setPreviewing(false);
    }
  };

  // ---- Clone a voice (ElevenLabs) -------------------------------------------
  const [cloneOpen, setCloneOpen] = useState(false);
  const [cloneName, setCloneName] = useState("");
  const [cloneGender, setCloneGender] = useState<"female" | "male">("female");
  const [cloneFiles, setCloneFiles] = useState<File[]>([]);
  const [cloneNoise, setCloneNoise] = useState(false);
  const [cloneConsent, setCloneConsent] = useState(false);
  const [cloneBusy, setCloneBusy] = useState(false);
  const [cloneMsg, setCloneMsg] = useState<{ ok: boolean; text: string } | null>(null);
  const cloneBytes = cloneFiles.reduce((n, f) => n + f.size, 0);
  const tooBig = cloneFiles.some((f) => f.size > 10 * 1024 * 1024);
  const submitClone = async () => {
    setCloneBusy(true);
    setCloneMsg(null);
    try {
      const created = await cloneVoice({
        name: cloneName.trim(),
        gender: cloneGender,
        removeBackgroundNoise: cloneNoise,
        consent: cloneConsent,
        files: cloneFiles,
      });
      await queryClient.invalidateQueries({ queryKey: ["ai-voice-engines"] });
      if (created?.id) {
        setEnginePick("elevenlabs");
        setEngineVoices((m) => ({ ...m, elevenlabs: created.id }));
      }
      setCloneOpen(false);
      setCloneFiles([]);
      setCloneConsent(false);
      setCloneMsg({
        ok: true,
        text: created?.requiresVerification
          ? ml ? "Voice ഉണ്ടാക്കി — ElevenLabs verification കഴിഞ്ഞാൽ ഉപയോഗിക്കാം." : "Voice created — usable once ElevenLabs finishes verifying it."
          : ml ? "Voice ready — ▶ അമർത്തി കേട്ടുനോക്കൂ." : "Voice ready — press ▶ to hear it.",
      });
    } catch (err) {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      setCloneMsg({ ok: false, text: String((err as any)?.response?.data?.message ?? (err as Error).message ?? "Could not create the voice.") });
    } finally {
      setCloneBusy(false);
    }
  };
  const removeVoice = async () => {
    if (!engineVoice?.custom) return;
    if (!window.confirm(ml ? `"${engineVoice.name}" delete ചെയ്യട്ടെ? ഇത് തിരിച്ചെടുക്കാൻ പറ്റില്ല.` : `Delete "${engineVoice.name}"? This cannot be undone.`)) return;
    setCloneMsg(null);
    try {
      await deleteClonedVoice(engineVoice.id);
      await queryClient.invalidateQueries({ queryKey: ["ai-voice-engines"] });
      setEngineVoices((m) => ({ ...m, elevenlabs: "" }));
    } catch (err) {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      setCloneMsg({ ok: false, text: String((err as any)?.response?.data?.message ?? (err as Error).message) });
    }
  };

  const canOverride = refusal?.code === "OUTSIDE_CALLING_HOURS" || refusal?.code === "DO_NOT_CALL";
  const objectiveMeta = objectives.find((o) => o.value === objective);
  const voiceMissing = engine !== "gemini" && !engineVoiceId;

  const submit = () => {
    stopPreview();
    writeLocal(AGENT_KEY, agentName.trim());
    writeLocal(VOICE_KEY, voice);
    writeLocal(DISCLOSE_KEY, discloseAI ? "1" : "0");
    writeLocal(DIALECT_KEY, dialect);
    writeLocal(GREETING_KEY, greeting);
    writeLocal(COMPANY_KEY, companyName.trim());
    writeLocal(SAYAS_KEY, companySayAs.trim());
    writeLocal(PACE_KEY, pace);
    if (engine !== "gemini") writeLocal(engineVoiceKey(engine), engineVoiceId);
    onStart({
      mode: "telecaller",
      leadId: 0, // filled by the overlay
      objective,
      brief: brief.trim() || undefined,
      agentName: agentName.trim() || undefined,
      voice: engine === "gemini" ? voice : gender,
      discloseAI,
      allowOutsideHours: override && refusal?.code === "OUTSIDE_CALLING_HOURS" ? true : undefined,
      allowDoNotCall: override && refusal?.code === "DO_NOT_CALL" ? true : undefined,
      practice: practice || undefined,
      dialect: language === "ml" ? dialect : undefined,
      greeting: language === "ml" && (dialect === "mappila" || dialect === "auto") ? greeting : undefined,
      companyName: companyName.trim() || undefined,
      companySayAs: companySayAs.trim() || undefined,
      pace,
      engine: engine !== "gemini" ? engine : undefined,
      engineVoice: engine !== "gemini" ? engineVoiceId : undefined,
    });
  };

  // Gemini Live has no speaking-rate knob and ignores pace instructions (measured
  // 2026-09-26), so the pace becomes a pitch-preserving stretch in the player
  // (voice/tempo.ts): natural 1×, brisk 1.15×, fast 1.3×, tunable during the call.
  // The custom voices play through the same player, so the same pace applies.
  const paceSelect = (
    <div>
      <div style={label}>{ml ? "സംസാരത്തിന്റെ speed" : "Pace"}</div>
      <select value={pace} onChange={(e) => setPace(e.target.value as Pace)} style={{ ...field, appearance: "auto" }}>
        <option value="brisk" style={{ color: "#111" }}>{ml ? "Brisk — കോഴിക്കോടൻ speed, 1.15× (default)" : "Brisk — the Kozhikode tempo, 1.15× (default)"}</option>
        <option value="fast" style={{ color: "#111" }}>{ml ? "Fast — ഇതിലും വേഗം, 1.3×" : "Fast — quicker still, 1.3×"}</option>
        <option value="natural" style={{ color: "#111" }}>{ml ? "Natural — സാധാരണ speed, 1×" : "Natural — as generated, 1×"}</option>
      </select>
    </div>
  );

  const customVoices = voiceList.filter((v) => v.custom);
  const stockVoices = voiceList.filter((v) => !v.custom);
  const voiceOption = (v: VoiceEngineVoice) => (
    <option key={v.id} value={v.id} style={{ color: "#111" }}>
      {v.name}
      {v.accent ? ` · ${v.accent}` : ""}
    </option>
  );
  const elevenError = engine === "elevenlabs" ? engineInfo("elevenlabs")?.error : undefined;

  return (
    <div className="xv-rec" style={{ display: "block", maxWidth: 560 }}>
      <div className="xv-rec-top" style={{ alignItems: "flex-start" }}>
        <div className="xv-rec-meta" style={{ flex: 1, minWidth: 0 }}>
          <div className="xv-rec-title">{ml ? `${leadName}-നെ വിളിക്കുന്നു` : `Calling ${leadName}`}</div>
          <div className="xv-rec-sub">
            {ml
              ? "Xale AI ഒരു telecaller ആയി ലീഡിനോട് സംസാരിക്കും. ഫോൺ speaker-ൽ വെക്കുക, അല്ലെങ്കിൽ ലീഡിന് കൈമാറുക."
              : "Xale AI speaks to the lead as your telecaller. Put the phone on speaker or hand it over."}
          </div>
        </div>
      </div>

      {refusal && (
        <div style={{ padding: "10px 12px", borderRadius: 12, background: "rgba(255, 132, 71, 0.14)", border: "1px solid rgba(255,132,71,0.35)", fontSize: 12.5, lineHeight: 1.45 }}>
          {refusal.message}
          {canOverride && (
            <label style={{ display: "flex", alignItems: "center", gap: 8, marginTop: 8, cursor: "pointer" }}>
              <input type="checkbox" checked={override} onChange={(e) => setOverride(e.target.checked)} />
              {refusal.code === "DO_NOT_CALL" ? (ml ? "ലീഡ് വീണ്ടും ബന്ധപ്പെട്ടു — എന്നാലും വിളിക്കുക" : "They contacted us again — call anyway") : ml ? "എന്നാലും വിളിക്കുക (ലീഡ് ഇപ്പോൾ വിളിക്കാൻ പറഞ്ഞു)" : "Call anyway (the lead asked to be called now)"}
            </label>
          )}
        </div>
      )}

      <div>
        <div style={label}>{ml ? "കോളിന്റെ ഉദ്ദേശ്യം" : "What is this call for"}</div>
        <select value={objective} onChange={(e) => setObjective(e.target.value as TelecallerObjective)} style={{ ...field, appearance: "auto" }}>
          {objectives.map((o) => (
            <option key={o.value} value={o.value} style={{ color: "#111" }}>
              {ml ? o.ml : o.en}
            </option>
          ))}
        </select>
        {objectiveMeta && <div className="xv-rec-sub" style={{ marginTop: 6 }}>{objectiveMeta.hint}</div>}
      </div>

      <div>
        <div style={label}>{objective === "custom" ? (ml ? "എന്ത് പറയണം" : "Brief for the AI") : ml ? "കൂടുതൽ നിർദ്ദേശം (optional)" : "Extra instructions (optional)"}</div>
        <textarea
          value={brief}
          onChange={(e) => setBrief(e.target.value.slice(0, 800))}
          rows={2}
          placeholder={ml ? "ഉദാ: ഒരു office visit fix ചെയ്യുക; അവർക്ക് പറ്റുന്ന time ചോദിക്കുക" : "e.g. Fix an office visit; ask what timing suits them"}
          style={{ ...field, height: "auto", padding: "10px 12px", resize: "vertical", lineHeight: 1.4 }}
        />
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
        <div>
          <div style={label}>{ml ? "AI-യുടെ പേര്" : "Caller name"}</div>
          <input value={agentName} onChange={(e) => setAgentName(e.target.value.slice(0, 40))} placeholder={gender === "male" ? "Arun" : "Anjali"} style={field} />
        </div>
        <div>
          <div style={label}>{ml ? "കമ്പനിയുടെ പേര് (പറയുന്നത് പോലെ)" : "Company name as spoken"}</div>
          <input value={companyName} onChange={(e) => setCompanyName(e.target.value.slice(0, 60))} placeholder={tenantName || "Skymark"} style={field} />
        </div>
        <div>
          <div style={label}>{ml ? "പറയുന്ന രീതി (English വാക്കുകളായി)" : "Say it as (plain English words)"}</div>
          <input value={companySayAs} onChange={(e) => setCompanySayAs(e.target.value.slice(0, 60))} placeholder="Sky Mark" style={field} />
        </div>
        <div>
          <div style={label}>{ml ? "ശബ്ദം — engine" : "Voice engine"}</div>
          <select
            value={engine}
            onChange={(e) => {
              stopPreview();
              setPreviewError(null);
              engineTouched.current = true;
              setEnginePick(e.target.value as VoiceEngineId);
            }}
            style={{ ...field, appearance: "auto" }}
          >
            {(["gemini", "sarvam", "elevenlabs"] as VoiceEngineId[]).map((id) => {
              const info = engineInfo(id);
              const available = id === "gemini" || !!info?.available;
              if (id !== "gemini" && !info) return null; // older server: Gemini only
              return (
                <option key={id} value={id} disabled={!available} style={{ color: "#111" }}>
                  {ENGINE_NAMES[id]}
                  {available ? "" : ml ? " (connect ചെയ്തിട്ടില്ല)" : " (not connected)"}
                </option>
              );
            })}
          </select>
        </div>
      </div>

      <div>
        <div style={label}>{ml ? "ശബ്ദം" : "Voice"}</div>
        <div style={{ display: "flex", gap: 8 }}>
          {engine === "gemini" ? (
            <select value={voice} onChange={(e) => setVoice(e.target.value)} style={{ ...field, appearance: "auto", flex: 1 }}>
              {VOICES.map((v) => (
                <option key={v.value} value={v.value} style={{ color: "#111" }}>
                  {ml ? v.ml : v.en}
                </option>
              ))}
            </select>
          ) : (
            <select value={engineVoiceId} onChange={(e) => setEngineVoice(e.target.value)} disabled={!voiceList.length} style={{ ...field, appearance: "auto", flex: 1 }}>
              {!voiceList.length && <option value="">{ml ? "Voice ഒന്നും ഇല്ല — താഴെ ഒന്ന് ഉണ്ടാക്കൂ" : "No voices yet — clone one below"}</option>}
              {engine === "sarvam" ? (
                <>
                  <optgroup label={ml ? "സ്ത്രീ" : "Female"}>{voiceList.filter((v) => v.gender === "female").map(voiceOption)}</optgroup>
                  <optgroup label={ml ? "പുരുഷൻ" : "Male"}>{voiceList.filter((v) => v.gender !== "female").map(voiceOption)}</optgroup>
                </>
              ) : (
                <>
                  {customVoices.length > 0 && <optgroup label={ml ? "നമ്മുടെ voices" : "Your voices"}>{customVoices.map(voiceOption)}</optgroup>}
                  {stockVoices.length > 0 && <optgroup label={ml ? "ElevenLabs voices" : "ElevenLabs stock voices"}>{stockVoices.map(voiceOption)}</optgroup>}
                </>
              )}
            </select>
          )}
          {serverHasEngines && (
            <button
              type="button"
              className="xv-pill"
              onClick={playPreview}
              disabled={voiceMissing}
              title={ml ? "ഒരു sample line കേൾക്കുക" : "Hear a sample line"}
              style={{ height: 40, padding: "0 14px", whiteSpace: "nowrap" }}
            >
              {previewing ? (ml ? "■ നിർത്തുക" : "■ Stop") : ml ? "▶ കേൾക്കുക" : "▶ Preview"}
            </button>
          )}
        </div>
        {previewError && <div style={{ ...errorNote, marginTop: 6 }}>{previewError}</div>}
        {elevenError && <div style={{ ...errorNote, marginTop: 6 }}>{elevenError}</div>}
        {engine !== "gemini" && (
          <div style={{ ...note, marginTop: 6 }}>
            {ml
              ? "Gemini കേൾക്കുകയും ചിന്തിക്കുകയും ചെയ്യും; അത് പറയുന്നത് ഈ voice-ൽ ലീഡ് കേൾക്കും. മറുപടി തുടങ്ങാൻ ഒരു നിമിഷം കൂടി എടുക്കാം."
              : "Gemini still listens and thinks; the lead hears its words in this voice. Replies may start a moment later."}
          </div>
        )}
        {engine === "elevenlabs" && (
          <div style={{ display: "flex", gap: 14, marginTop: 8 }}>
            <button type="button" style={linkBtn} onClick={() => { setCloneOpen((o) => !o); setCloneMsg(null); }}>
              {cloneOpen ? (ml ? "Clone ചെയ്യുന്നത് വേണ്ട" : "Close voice cloning") : ml ? "+ പുതിയ voice clone ചെയ്യുക" : "+ Clone a new voice"}
            </button>
            {engineVoice?.custom && !cloneOpen && (
              <button type="button" style={linkBtn} onClick={removeVoice}>
                {ml ? "ഈ voice delete ചെയ്യുക" : "Delete this voice"}
              </button>
            )}
          </div>
        )}
        {cloneMsg && <div style={{ ...(cloneMsg.ok ? note : errorNote), marginTop: 6 }}>{cloneMsg.text}</div>}
      </div>

      {engine === "elevenlabs" && cloneOpen && (
        <div style={panel}>
          <div style={note}>
            {ml
              ? "ഒരാളുടെ 1–3 മിനിറ്റ് clear ആയ സംസാരം (മലയാളം, music ഇല്ലാതെ, ഒരാൾ മാത്രം) upload ചെയ്യൂ. MP3 / WAV / M4A, ഒരു file 10 MB വരെ, 5 files വരെ."
              : "Upload 1–3 minutes of one person speaking clearly (Malayalam, no music, no other voices). MP3 / WAV / M4A, up to 10 MB each, up to 5 files."}
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "2fr 1fr", gap: 10 }}>
            <div>
              <div style={label}>{ml ? "Voice-ന്റെ പേര്" : "Voice name"}</div>
              <input value={cloneName} onChange={(e) => setCloneName(e.target.value.slice(0, 60))} placeholder={ml ? "ഉദാ: Shifana — Kozhikode" : "e.g. Shifana — Kozhikode"} style={field} />
            </div>
            <div>
              <div style={label}>{ml ? "ശബ്ദം" : "Gender"}</div>
              <select value={cloneGender} onChange={(e) => setCloneGender(e.target.value as "female" | "male")} style={{ ...field, appearance: "auto" }}>
                <option value="female" style={{ color: "#111" }}>{ml ? "സ്ത്രീ" : "Female"}</option>
                <option value="male" style={{ color: "#111" }}>{ml ? "പുരുഷൻ" : "Male"}</option>
              </select>
            </div>
          </div>
          <div>
            <input
              type="file"
              accept="audio/*,video/mp4,video/webm"
              multiple
              onChange={(e) => setCloneFiles(Array.from(e.target.files ?? []).slice(0, 5))}
              style={{ fontSize: 12.5 }}
            />
            {cloneFiles.length > 0 && (
              <div style={{ ...(tooBig ? errorNote : note), marginTop: 4 }}>
                {cloneFiles.length} {ml ? "file" : cloneFiles.length === 1 ? "file" : "files"} · {(cloneBytes / (1024 * 1024)).toFixed(1)} MB
                {tooBig ? (ml ? " — ഒരു file 10 MB-ൽ കൂടുതൽ ആണ്" : " — one file is over 10 MB") : ""}
              </div>
            )}
          </div>
          <label style={{ display: "flex", alignItems: "flex-start", gap: 8, fontSize: 12.5, lineHeight: 1.4, cursor: "pointer" }}>
            <input type="checkbox" checked={cloneNoise} onChange={(e) => setCloneNoise(e.target.checked)} style={{ marginTop: 3 }} />
            <span>{ml ? "Background noise മാറ്റുക (recording-ൽ noise ഉണ്ടെങ്കിൽ മാത്രം)" : "Remove background noise (only if the recording is noisy)"}</span>
          </label>
          <label style={{ display: "flex", alignItems: "flex-start", gap: 8, fontSize: 12.5, lineHeight: 1.4, cursor: "pointer" }}>
            <input type="checkbox" checked={cloneConsent} onChange={(e) => setCloneConsent(e.target.checked)} style={{ marginTop: 3 }} />
            <span>
              <strong>{ml ? "ഈ ശബ്ദം ഉള്ള ആൾ എഴുതി സമ്മതം തന്നിട്ടുണ്ട്" : "The speaker has given written permission"}</strong>
              <span style={{ display: "block", opacity: 0.7 }}>
                {ml ? "അവരുടെ ശബ്ദം AI calls-ന് ഉപയോഗിക്കാൻ." : "to use a clone of their voice for AI calls."}
              </span>
            </span>
          </label>
          <div style={{ display: "flex", justifyContent: "flex-end" }}>
            <button
              type="button"
              className="xv-pill is-primary"
              onClick={submitClone}
              disabled={cloneBusy || !cloneName.trim() || !cloneFiles.length || !cloneConsent || tooBig}
              style={{ height: 36, padding: "0 16px" }}
            >
              {cloneBusy ? (ml ? "ഉണ്ടാക്കുന്നു…" : "Creating…") : ml ? "Voice ഉണ്ടാക്കുക" : "Create voice"}
            </button>
          </div>
        </div>
      )}

      {ml && (
        <div style={{ display: "grid", gridTemplateColumns: dialect === "mappila" || dialect === "auto" ? "1fr 1fr 1fr" : "1fr 1fr", gap: 10 }}>
          <div>
            <div style={label}>മലയാളം flavour</div>
            <select value={dialect} onChange={(e) => setDialect(e.target.value as Dialect)} style={{ ...field, appearance: "auto" }}>
              <option value="auto" style={{ color: "#111" }}>Auto — branch അനുസരിച്ച് (മലബാർ → Mappila)</option>
              <option value="mappila" style={{ color: "#111" }}>കോഴിക്കോട് Mappila — ഞമ്മള്, ഇങ്ങള്, ഇക്കാ, ഇൻശാ അല്ലാഹ്</option>
              <option value="kozhikode" style={{ color: "#111" }}>കോഴിക്കോട് general — ഇണ്ട്, ഇപ്പം, ന്നാ, …ട്ടോ</option>
              <option value="standard" style={{ color: "#111" }}>Standard — കൊച്ചി / തൃശ്ശൂർ</option>
            </select>
          </div>
          {(dialect === "mappila" || dialect === "auto") && (
            <div>
              <div style={label}>{ml ? "തുടക്കം" : "Greeting"}</div>
              <select value={greeting} onChange={(e) => setGreeting(e.target.value as Greeting)} style={{ ...field, appearance: "auto" }}>
                <option value="auto" style={{ color: "#111" }}>Auto — പേര് നോക്കി</option>
                <option value="salaam" style={{ color: "#111" }}>അസ്സലാമു അലൈക്കും</option>
                <option value="hello" style={{ color: "#111" }}>ഹലോ (neutral)</option>
              </select>
            </div>
          )}
          {paceSelect}
        </div>
      )}
      {!ml && paceSelect}

      <label style={{ display: "flex", alignItems: "flex-start", gap: 8, fontSize: 12.5, lineHeight: 1.4, cursor: "pointer" }}>
        <input type="checkbox" checked={discloseAI} onChange={(e) => setDiscloseAI(e.target.checked)} style={{ marginTop: 3 }} />
        <span>
          {ml ? "തുടക്കത്തിൽ തന്നെ AI അസിസ്റ്റന്റ് ആണെന്ന് പറയുക (ശുപാർശ)" : "Say up front that the caller is an AI assistant (recommended)"}
          <span style={{ display: "block", opacity: 0.6 }}>{ml ? "ചോദിച്ചാൽ എപ്പോഴും സത്യം പറയും." : "It always answers honestly when asked."}</span>
        </span>
      </label>

      <label
        style={{
          display: "flex", alignItems: "flex-start", gap: 8, fontSize: 12.5, lineHeight: 1.4, cursor: "pointer",
          padding: "10px 12px", borderRadius: 12,
          background: practice ? "rgba(255, 178, 122, 0.14)" : "rgba(255,255,255,0.05)",
          border: `1px solid ${practice ? "rgba(255,178,122,0.45)" : "rgba(255,255,255,0.12)"}`,
        }}
      >
        <input type="checkbox" checked={practice} onChange={(e) => setPractice(e.target.checked)} style={{ marginTop: 3 }} />
        <span>
          <strong>{ml ? "പ്രാക്ടീസ് കോൾ — ഞാൻ ലീഡ് ആയി സംസാരിക്കും" : "Practice call — I will play the lead"}</strong>
          <span style={{ display: "block", opacity: 0.7 }}>
            {ml
              ? `AI ${leadName}-നോട് സംസാരിക്കുന്നത് പോലെ തന്നെ നിങ്ങളോട് സംസാരിക്കും. Note, follow-up — ഒന്നും save ചെയ്യില്ല. Calling hours ബാധകമല്ല.`
              : `The AI talks to you exactly as it would to ${leadName}. No note, no follow-up, nothing is saved. Calling hours do not apply.`}
          </span>
        </span>
      </label>

      <div style={{ display: "flex", gap: 10, justifyContent: "flex-end", marginTop: 2 }}>
        <button className="xv-pill" onClick={() => { stopPreview(); onCancel(); }} style={{ height: 40, padding: "0 16px" }}>
          {ml ? "വേണ്ട" : "Cancel"}
        </button>
        <button
          className="xv-pill is-primary"
          onClick={submit}
          disabled={(!practice && !!refusal && canOverride && !override) || voiceMissing}
          style={{ height: 40, padding: "0 20px" }}
        >
          {practice ? (ml ? "പ്രാക്ടീസ് തുടങ്ങുക" : "Start practice") : ml ? "വിളിക്കുക" : "Start the call"}
        </button>
      </div>
    </div>
  );
}
